import type { IntakeFields } from './aiIntake'

/**
 * Areas in the review: a number and the unit it is shown in.
 *
 * The listing keeps every area in square feet, so that is what the draft's
 * `landArea`, `builtUp` and `sizeOfLand` hold, and square feet is how each is
 * shown. A title gives hectares or acres, and the API has already converted
 * that. The agent may pick another unit to read the figure in, from the units
 * the listing form itself offers; picking one converts the figure, it does
 * not change the land. The unit picked lives on the draft as
 * `<key>DisplayUnit`.
 */
export type AreaUnit = 'sqft' | 'sqm' | 'acre' | 'hectare'
export type AreaKey = 'landArea' | 'builtUp' | 'sizeOfLand'
export interface UnitChoice {
    value: AreaUnit
    label: string
}

export const SQFT_PER: Record<AreaUnit, number> = { sqft: 1, sqm: 10.7639, acre: 43560, hectare: 107639 }

/** Decimal places worth showing in each unit: nobody quotes a fraction of a square foot. */
const PLACES: Record<AreaUnit, number> = { sqft: 0, sqm: 0, acre: 4, hectare: 4 }

export const SQUARE_FEET: UnitChoice = { value: 'sqft', label: 'Square feet' }

/** How the listing form's unit lists name each unit. */
const KNOWN: Record<string, AreaUnit> = { sqft: 'sqft', sf: 'sqft', ft2: 'sqft', sqm: 'sqm', m2: 'sqm', acre: 'acre', acres: 'acre', hectare: 'hectare', hectares: 'hectare', ha: 'hectare' }

/**
 * The units offered: exactly the listing form's list for this field
 * (`landAreaUnits` or `builtUpUnits` from /listings/criterias), with its own
 * labels. Square feet alone until that list arrives, or if it names nothing
 * this screen can convert.
 */
export function unitChoices(list: unknown): UnitChoice[] {
    const rows = Array.isArray(list) ? list : []
    const out: UnitChoice[] = []
    for (const r of rows) {
        const row = r as { value?: unknown; label?: unknown }
        const unit = KNOWN[String(row?.value ?? '').toLowerCase()]
        if (unit && !out.some((u) => u.value === unit)) out.push({ value: unit, label: typeof row.label === 'string' && row.label ? row.label : unit })
    }
    return out.length ? out : [SQUARE_FEET]
}

export const toSqft = (value: number, unit: AreaUnit) => Math.round(value * SQFT_PER[unit])

/** Square feet as a figure in another unit, rounded to what that unit is quoted to. */
export const fromSqft = (sqft: number, unit: AreaUnit) => Number((sqft / SQFT_PER[unit]).toFixed(PLACES[unit]))

/** How each unit is written, in English and on a Malay title. */
const UNIT_WORDS: [AreaUnit, RegExp][] = [
    ['sqm', /(meter\s*persegi|metre\s*persegi|square\s*met(?:er|re)s?|sq\.?\s*m(?:eters?|etres?)?\b|sqm\b|m2\b|m²|\bmp\b)/i],
    ['sqft', /(kaki\s*persegi|square\s*f(?:ee|oo)t|sq\.?\s*f(?:ee|oo)?t\b|sqft\b|ft2\b|ft²|\bkps\b|\bsf\b)/i],
    ['hectare', /\b(hectares?|hektar|ha)\b/i],
    ['acre', /\b(acres?|ekar|ac)\b/i],
]

/** "0.1859 hektar" is 0.1859 hectare. For drafts saved when the size was a line of text. */
export function parseArea(text: unknown): { value: number; unit: AreaUnit } | null {
    const raw = String(text ?? '')
    const found = raw.match(/\d[\d,]*(?:\.\d+)?/)
    if (!found) return null
    const value = Number(found[0].replace(/,/g, ''))
    if (!Number.isFinite(value) || value <= 0) return null
    return { value, unit: UNIT_WORDS.find(([, pattern]) => pattern.test(raw))?.[0] ?? 'sqft' }
}

export interface AreaReading {
    /** The figure as shown, in `unit`. Null while the field is empty. */
    value: number | null
    unit: AreaUnit
    /** What the listing stores. */
    sqft: number | null
}

/**
 * The square feet the draft holds for a field.
 *
 * The size of land on the title is the land area unless the agent says
 * otherwise, so it follows the land area until it is typed in; from then on
 * it is its own value, even when it is cleared.
 */
export function sqftOf(fields: IntakeFields, key: AreaKey): number | null {
    const stored = fields[key]
    if (typeof stored === 'number' && stored > 0) return Math.round(stored)
    if (key !== 'sizeOfLand' || Object.hasOwn(fields, 'sizeOfLand')) return null
    const land = sqftOf(fields, 'landArea')
    if (land !== null) return land
    const written = parseArea(fields.landAreaStated)
    return written ? toSqft(written.value, written.unit) : null
}

/** What the field shows: square feet, unless the agent picked another of the units on offer. */
export function areaReading(fields: IntakeFields, key: AreaKey, units: UnitChoice[] = [SQUARE_FEET]): AreaReading {
    const picked = fields[`${key}DisplayUnit`]
    const unit = units.find((u) => u.value === picked)?.value ?? units.find((u) => u.value === 'sqft')?.value ?? units[0].value
    const sqft = sqftOf(fields, key)
    return { value: sqft === null ? null : fromSqft(sqft, unit), unit, sqft }
}

/** The draft fields for a figure typed in a unit: the square feet it comes to, and the unit it is shown in. */
export function areaFields(key: AreaKey, value: number | null, unit: AreaUnit): IntakeFields {
    const usable = typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
    return { [key]: usable === null ? null : toSqft(usable, unit), [`${key}DisplayUnit`]: unit }
}

/** Another unit picked: the land is the same, so only how it is shown changes, and the figure converts. */
export function areaUnitFields(key: AreaKey, fields: IntakeFields, unit: AreaUnit): IntakeFields {
    const sqft = sqftOf(fields, key)
    // The size of land was following the land area; showing it in a unit makes it its own.
    return { [`${key}DisplayUnit`]: unit, ...(key === 'sizeOfLand' && !Object.hasOwn(fields, 'sizeOfLand') && sqft !== null ? { sizeOfLand: sqft } : {}) }
}
